<script lang="ts">
  import type { HTMLAttributes } from 'vue'

  /**
   * An item in a `SelectInput`. The generic `TMeta` lets callers attach a
   * typed payload (e.g. the full domain object) they can read back in the
   * `#item` slot without losing type safety.
   */
  export interface SelectInputItem<TMeta = unknown> {
    value: string
    title: string
    /** Extra text the Command filter searches against (in addition to title). */
    searchValue?: string
    /** Caller-typed payload available in the `#item` slot via its scope. */
    metaData?: TMeta
    disabled?: boolean
  }

  /**
   * Configuration forwarded to the underlying popover dropdown.
   */
  export interface SelectInputPopoverProps {
    align?: 'start' | 'center' | 'end'
    /** `number` = fixed px width, `'trigger'` = match the trigger element. */
    width?: number | 'trigger'
    placeholderSearch?: string
    noItemText?: string
    /** Extra class forwarded to the dropdown `PopoverContent`. */
    class?: HTMLAttributes['class']
  }
</script>

<script setup lang="ts" generic="TMeta = unknown">
  import { computed, useTemplateRef } from 'vue'
  import { IconCheck, IconChevronDown, IconClose } from '@gat-ship/icons'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
  } from '../command'
  import { Popover, PopoverContent, PopoverTrigger } from '../popover'
  import { cn } from '../../../lib/utils'

  /**
   * Standalone searchable dropdown control, decoupled from any form
   * library. Built on `ui/popover` + `ui/command` (NOT `ui/select`, which
   * is a native-style picker with no search). Drop it into a `FormField`
   * default slot with `input-group="false"` to get label / error chrome.
   *
   * Extracted from the old `FormSelectField` so visuals stay identical;
   * the form wiring (value, change, blur, invalid) is passed in as props /
   * emits instead of read off a tanstack field.
   */
  const props = defineProps<{
    items: SelectInputItem<TMeta>[]
    modelValue?: string
    /** Forwarded to the trigger `id` for label association. */
    id?: string
    placeholder?: string
    dataTestid?: string
    disabled?: boolean
    /** Async-loading the items list itself (not the value). */
    loading?: boolean
    /** Reflected on the trigger as `aria-invalid` for the error ring. */
    ariaInvalid?: boolean
    /** When false, the clear button is never shown (mirrors `required`). */
    clearable?: boolean
    /**
     * Whether to show a search/filter input at the top of the dropdown.
     * Default `true`. Only rendered when `items.length > 6` anyway.
     */
    showSearch?: boolean
    popoverProps?: SelectInputPopoverProps
    inputClass?: HTMLAttributes['class']
    /**
     * When set, the default trigger renders as a stacked field-box —
     * label inside on top, the selected value below — matching
     * `DatePicker`'s `label` variant. Also names the clear button for
     * screen readers.
     */
    label?: string
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
    (e: 'blur'): void
  }>()

  defineSlots<{
    trigger?: (props: {
      item: SelectInputItem<TMeta> | undefined
      placeholder: string | undefined
      isOpen: boolean
    }) => unknown
    item?: (props: {
      item: SelectInputItem<TMeta>
      selected: boolean
    }) => unknown
    /** Custom display for the selected value inside the default trigger button. */
    selected?: (props: { item: SelectInputItem<TMeta> }) => unknown
    /** Replaces the entire dropdown body; falls back to the built-in command. */
    content?: () => unknown
    /** Rendered inside the command list, before the items. */
    'prepend-content-item'?: () => unknown
    /** Rendered inside the command list, after the items. */
    'append-content-item'?: () => unknown
  }>()

  /** Controllable open state — `v-model:open` from the consumer, internal otherwise. */
  const isOpen = defineModel<boolean>('open', { default: false })
  function setOpen(next: boolean) {
    if (next && props.disabled) return
    isOpen.value = next
  }

  const triggerWrapper = useTemplateRef<HTMLElement>('triggerWrapper')

  const resolvedPopoverWidth = computed<string | undefined>(() => {
    const w = props.popoverProps?.width
    if (w === undefined) return undefined
    if (w === 'trigger') {
      const el = triggerWrapper.value
      return el ? `${el.offsetWidth}px` : undefined
    }
    return `${w}px`
  })

  function findSelected(
    value: string | undefined
  ): SelectInputItem<TMeta> | undefined {
    if (value === undefined || value === '') return undefined
    return props.items.find((i) => i.value === value)
  }

  function handleSelect(item: SelectInputItem<TMeta>) {
    if (item.disabled) return
    emit('update:modelValue', item.value)
    emit('blur')
    setOpen(false)
  }

  function handleClear(event: Event) {
    event.stopPropagation()
    emit('update:modelValue', '')
    emit('blur')
  }

  const shouldShowSearch = computed(
    () => (props.showSearch ?? true) && props.items.length > 6
  )
  const searchPlaceholder = computed(
    () => props.popoverProps?.placeholderSearch ?? 'Search…'
  )
  const noItemText = computed(
    () => props.popoverProps?.noItemText ?? 'No results'
  )
</script>

<template>
  <Popover
    :open="isOpen"
    @update:open="
      (v) => {
        setOpen(v)
        if (!v) emit('blur')
      }
    "
  >
    <PopoverTrigger as-child>
      <span ref="triggerWrapper" class="inline-flex w-full min-w-0">
        <slot
          name="trigger"
          :item="findSelected(modelValue)"
          :placeholder="placeholder"
          :is-open="isOpen"
        >
          <button
            :id="id"
            type="button"
            data-slot="select-trigger"
            :disabled="disabled"
            :aria-invalid="ariaInvalid || undefined"
            :aria-busy="loading || undefined"
            :data-testid="dataTestid"
            :class="
              cn(
                'group/select-trigger w-full min-w-0 rounded-md border px-3 text-left text-sm outline-none transition-[color,box-shadow]',
                'border-input',
                label
                  ? 'bg-background flex flex-col items-start gap-0.5 py-1.5'
                  : 'dark:bg-input/30 flex h-9 items-center justify-between gap-2 bg-transparent py-1',
                'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
                ariaInvalid &&
                  'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
                inputClass
              )
            "
          >
            <span
              v-if="label"
              class="text-xs font-medium leading-none text-neutral-700 dark:text-neutral-200"
            >
              {{ label }}
            </span>
            <span
              class="flex w-full min-w-0 items-center justify-between gap-2"
            >
              <span
                :class="[
                  'flex-1 truncate',
                  !modelValue && 'text-neutral-400 dark:text-neutral-500'
                ]"
              >
                <template v-if="loading">Loading…</template>
                <template v-else-if="findSelected(modelValue)">
                  <slot name="selected" :item="findSelected(modelValue)!">
                    {{ findSelected(modelValue)?.title }}
                  </slot>
                </template>
                <template v-else>{{ placeholder }}</template>
              </span>
              <span class="flex shrink-0 items-center gap-1">
                <button
                  v-if="clearable && modelValue && !disabled && !loading"
                  type="button"
                  tabindex="-1"
                  class="text-muted-foreground hover:text-foreground hidden size-4 items-center justify-center rounded-sm group-hover/select-trigger:inline-flex"
                  :aria-label="`Clear ${label ?? 'field'}`"
                  @click.stop="handleClear"
                >
                  <IconClose class="size-3.5" />
                </button>
                <IconChevronDown
                  class="text-muted-foreground size-4 shrink-0 opacity-50"
                />
              </span>
            </span>
          </button>
        </slot>
      </span>
    </PopoverTrigger>

    <PopoverContent
      :align="popoverProps?.align ?? 'start'"
      :side-offset="1"
      :collision-padding="8"
      :class="cn('p-0', popoverProps?.class)"
      :style="{
        width: resolvedPopoverWidth ?? `${triggerWrapper?.offsetWidth}px`
      }"
    >
      <slot name="content">
        <Command :should-filter="shouldShowSearch">
          <CommandInput
            v-if="shouldShowSearch"
            :placeholder="searchPlaceholder"
          />
          <CommandList>
            <CommandEmpty>{{ noItemText }}</CommandEmpty>
            <CommandGroup>
              <slot name="prepend-content-item" />
              <CommandItem
                v-for="item in items"
                :key="item.value"
                :value="
                  [item.value, item.title, item.searchValue]
                    .filter(Boolean)
                    .join(' ')
                "
                :disabled="item.disabled"
                :data-testid="`combobox-item-${item.value}`"
                @select="() => handleSelect(item)"
              >
                <slot
                  name="item"
                  :item="item"
                  :selected="modelValue === item.value"
                >
                  <span class="flex-1 truncate">{{ item.title }}</span>
                  <IconCheck
                    :class="[
                      'text-primary ml-auto size-4 shrink-0',
                      modelValue === item.value ? 'opacity-100' : 'opacity-0'
                    ]"
                  />
                </slot>
              </CommandItem>
              <slot name="append-content-item" />
            </CommandGroup>
          </CommandList>
        </Command>
      </slot>
    </PopoverContent>
  </Popover>
</template>
